import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Camera, ChefHat, Dumbbell, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import nutreTeLogo from "@/assets/nutre-te-logo.png";

const features = [
  { icon: Camera, title: "Escanea tu comida", desc: "Toma una foto y obtén calorías al instante con IA" },
  { icon: ChefHat, title: "Recetas saludables", desc: "Recetas personalizadas según tu objetivo" },
  { icon: Dumbbell, title: "Rutinas de ejercicio", desc: "Quema el exceso de calorías con rutinas IA" },
];

const Index = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // Capturar errores de OAuth que llegan a la raíz con hash (#error=...)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash;
    if (hash && hash.includes("error=")) {
      const params = new URLSearchParams(hash.replace(/^#/, ""));
      const desc = params.get("error_description") || params.get("error") || "Error de autenticación";
      // Limpiar el hash y redirigir al login con el mensaje
      window.history.replaceState(null, "", window.location.pathname);
      navigate(`/auth?oauth_error=${encodeURIComponent(desc)}`, { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard");
  }, [user, loading]);

  return (
    <div className="min-h-screen gradient-hero">
      <div className="max-w-lg mx-auto px-6 py-12 flex flex-col min-h-screen">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex-1 flex flex-col justify-center text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
            className="inline-flex items-center justify-center mx-auto mb-6"
          >
            <img src={nutreTeLogo} alt="Nutre-Te" width={112} height={112} />
          </motion.div>

          <h1 className="text-4xl font-display font-bold text-foreground mb-3">Nutre-Te</h1>
          <p className="text-lg text-muted-foreground mb-8 max-w-xs mx-auto">
            Cuenta calorías con solo una foto. Tu nutricionista con inteligencia artificial.
          </p>

          <div className="space-y-3 mb-8">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.1 }}
                className="flex items-center gap-4 p-4 rounded-2xl bg-card shadow-card text-left"
              >
                <div className="w-11 h-11 rounded-xl gradient-primary flex items-center justify-center shrink-0">
                  <f.icon className="w-5 h-5 text-primary-foreground" />
                </div>
                <div>
                  <p className="font-medium text-foreground text-sm">{f.title}</p>
                  <p className="text-xs text-muted-foreground">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="space-y-3 pb-8">
          <Button onClick={() => navigate("/auth")} className="w-full h-14 text-lg gradient-primary text-primary-foreground rounded-2xl">
            Comenzar gratis
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.div>

        <footer className="pb-6 pt-2 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          
          <Link to="/terms" className="hover:text-foreground transition-colors">Términos</Link>
          <Link to="/privacy" className="hover:text-foreground transition-colors">Privacidad</Link>
        </footer>
      </div>
    </div>
  );
};

export default Index;
